import { EventEmitter } from 'events';
import { mergeWith, merge, get, set, isString, template } from 'lodash-es';
import { ref, type Ref } from 'vue';
import debug from 'debug';
import { Auth, Authz, FakeAuth } from './auth';
import { Connections } from './connections';
import { DocumentTitle } from './document-title';
import { fns, GlobalHelpers } from './fn';
import { traverse, translateGettextExpressions } from './helpers';
import { I18n } from './i18n';
import { Preferences } from './preferences';
import { ObjectStore } from './object-store';
import { ApplicationSpec, AppConfig } from './schemas';
import { Users } from './users';
import { deepCopy } from './helpers';
import { Repository } from './repo';

const log = debug('itp:core:application');

const env = globalThis.__INTEGRO_ENV__ || {};
const vars = env.vars || {};
const NODE_ENV = env.NODE_ENV;
const AUTH_TOKEN = env.AUTH_TOKEN;

export const CONFIGURATION = {};

import {
  AppLayoutManager,
  AVAILABLE_LAYOUTS,
  DEFAULT_APP_LAYOUT,
  isAppLayoutCode,
  type AppLayoutCode,
  type AppLayoutOption,
} from './app-layout';

export {
  AppLayoutManager,
  AVAILABLE_LAYOUTS,
  DEFAULT_APP_LAYOUT,
  isAppLayoutCode,
};
export type { AppLayoutCode, AppLayoutOption };

export function processApplicationSpec(
  app: ApplicationSpec,
  vars: Dictionary<any>,
) {
  function process(value: any, key: string | number, obj: any) {
    if (isString(value) && value.indexOf('<%=') > -1) {
      obj[key] = template(value)(vars);
      return;
    }
  }
  traverse(app.connections, process, 1);
  return app;
}

export abstract class BaseApplication {
  public ready: boolean = false;
  private readyPromise: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;

  readonly vars: Dictionary<any>;
  readonly data: object;
  readonly spec: ApplicationSpec;

  readonly auth: Authz;
  readonly connections: Connections;
  readonly documentTitle: DocumentTitle;
  readonly i18n: I18n;
  readonly preferences: Preferences;
  readonly users: Users;
  readonly fn: GlobalHelpers;
  readonly objectStore: ObjectStore;
  readonly config: AppConfig;
  readonly events: EventEmitter;
  readonly repo: Repository;

  private _confirmPageCloseRef: Ref<boolean> = ref(false);

  get confirmPageClose(): boolean {
    return this._confirmPageCloseRef.value;
  }

  set confirmPageClose(value: boolean) {
    const next = !!value;
    if (this._confirmPageCloseRef.value === next) {
      return;
    }
    this._confirmPageCloseRef.value = next;
    if (this.objectStore) {
      this.objectStore.setItem('app.confirmPageClose', { value: next });
    }
  }

  layoutManager!: AppLayoutManager;

  get layout(): AppLayoutCode {
    return this.layoutManager.value;
  }

  set layout(value: AppLayoutCode) {
    this.layoutManager.value = value;
  }

  setLayout(value: AppLayoutCode) {
    this.layout = value;
  }

  get layoutMode(): AppLayoutCode {
    return this.layoutManager.value;
  }

  PACKAGE_VERSION = env.PACKAGE_VERSION;
  PRODUCT_VERSION = env.PRODUCT_VERSION;

  get document() {
    return typeof window !== 'undefined' ? window.document : undefined;
  }

  waitForReady(): Promise<void> {
    if (this.ready) {
      return Promise.resolve();
    }
    if (!this.readyPromise) {
      this.readyPromise = new Promise((resolve) => {
        this.readyResolve = resolve;
      });
    }
    return this.readyPromise;
  }

  protected setReady() {
    this.ready = true;
    if (this.readyResolve) {
      this.readyResolve();
      this.readyResolve = null;
    }
  }

  constructor(app: ApplicationSpec) {
    log('initializing base app... %o', app);
    processApplicationSpec(app, mergeWith({}, app.defaultVars, vars));
    this.events = new EventEmitter();

    if (typeof window !== 'undefined') {
      set(window, '$app', this);
    }

    this.vars = vars as unknown as Dictionary<any>;

    this.i18n = new I18n({
      storageKey: app.name,
      translations: app.translations,
      supportedLocales: app.supportedLocales,
    });

    this.preferences = new Preferences();

    this.documentTitle = new DocumentTitle('Integro', ' | Integro');
    this.objectStore = new ObjectStore(app.name);

    const storedConfirmPageClose = this.objectStore.getItem(
      'app.confirmPageClose',
      null,
    );
    if (
      storedConfirmPageClose &&
      typeof storedConfirmPageClose === 'object' &&
      typeof (storedConfirmPageClose as { value?: unknown }).value === 'boolean'
    ) {
      this._confirmPageCloseRef.value = (
        storedConfirmPageClose as { value: boolean }
      ).value;
    }

    this.layoutManager = new AppLayoutManager(this.objectStore);

    this.auth = this.createAuth();

    this.data = app.data ? { ...app.data } : {};
    this.spec = app;
    this.config = translateGettextExpressions(
      merge(deepCopy(CONFIGURATION), app.config || {}),
      this.i18n,
    );

    this.connections = new Connections(this.i18n.LCID);

    this.users = new Users(this as any);
    this.fn = fns(this as any);
    this.repo = new Repository(this.connections);

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', (e) => {
        if (this.confirmPageClose) {
          this.onBeforeUnload(e);
        }
      });
    }
  }

  abstract mount(selector: string): void;

  toggleConfirmPageClose(event?: Event | boolean) {
    if (typeof event === 'boolean') {
      this.confirmPageClose = event;
    } else if (event && event.target instanceof HTMLInputElement) {
      this.confirmPageClose = event.target.checked;
    } else {
      this.confirmPageClose = !this.confirmPageClose;
    }
  }

  onBeforeUnload(e: BeforeUnloadEvent) {
    e.preventDefault();
    e.returnValue = '';
  }

  get isFakeAuth() {
    return NODE_ENV === 'development' && !!AUTH_TOKEN;
  }

  protected createAuth(): Authz {
    if (this.isFakeAuth) {
      log('Faking auth based on ENV settings with AUTH_TOKEN');
      return new FakeAuth({
        token: AUTH_TOKEN!,
        accountUrl: vars.auth?.accountUrl,
      });
    }

    const authVars = this.getAuthVars();
    log('Instantiating Keycloak auth with %o', authVars);
    return new Auth(authVars);
  }

  protected getAuthVars(): {
    realm: string;
    url?: string;
    path?: string;
    clientId: string;
  } {
    const result: any = { ...(vars.auth || {}) };

    const realm = this.getMetaContent('kc-realm');
    if (realm) {
      result.realm = realm;
    }

    const url = this.getMetaContent('kc-url');
    if (url) {
      result.url = url;
    }

    return result;
  }

  private getMetaContent(name: string): string | undefined {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const el = document.querySelector(`meta[name="${name}"]`);
    if (el instanceof HTMLMetaElement && el.content) {
      return el.content;
    }
    return undefined;
  }

  static configure(configuration: Dictionary<any>) {
    merge(CONFIGURATION, configuration);
  }
}
